import { Button } from "react-bootstrap";
import DualPriceCell from 'components/others/DualPriceCell';
import { calcSaleRate, saleUsdFromEditValue } from 'utils/helpers/currency/dualPrice';
import { ProductTitle } from 'modules/product';
import CountBadge from "../CounBadge";
import { safeIntParse } from "utils/helpers/safeParsing";
import { useSettings } from "context/SettingsContext";
import { useState, type Dispatch, type MouseEvent, type SetStateAction } from "react";
import FormInput from "components/forms/FormInput";
import { useTranslation } from "react-i18next";
import type { WarehouseComingRowType } from "./model/types";
import SolarIcon from 'components/icons/SolarIcon';

interface ComingTableBodyProps {
    aComing: WarehouseComingRowType[];
    setAComing: Dispatch<SetStateAction<WarehouseComingRowType[]>>;
    comingProcess: boolean;
    editable?: boolean;
    showPayment?: boolean;
    hidePrice?: boolean;
    getMaxCount?: (row: WarehouseComingRowType) => number | null | undefined;
}

const ComingTableBody = ({
    aComing,
    setAComing,
    comingProcess,
    editable = true,
    showPayment = false,
    hidePrice = false,
    getMaxCount,
}: ComingTableBodyProps) => {
    const { localSettings } = useSettings();
    const { t } = useTranslation(['Warehouses', 'common']);
    const rate = Number(localSettings?.RATE?.current || 1);
    const [editField, setEditField] = useState<{ index: number; field: 'name' | 'article' | 'price' | 'count' } | null>(null);
    const [editRatePrice, setEditRatePrice] = useState(false);

    const handleClickDelete = (e: MouseEvent<HTMLButtonElement>, index: number) => {
        setAComing((prev) => prev.filter((_, i) => i !== index));
        setEditField((prev) => {
            if (!prev) return null;
            if (prev.index === index) return null;
            if (prev.index > index) return { ...prev, index: prev.index - 1 };
            return prev;
        });
        e.currentTarget.blur();
    }

    const handleChangeRow = (index: number, patch: Partial<WarehouseComingRowType['coming']>) => {
        setAComing((prev) =>
            prev.map((row, i) =>
                i !== index
                    ? row
                    : {
                          ...row,
                          coming: {
                              ...row.coming,
                              ...patch,
                          },
                      },
            ),
        );
    };

    const handleChangeProduct = (index: number, patch: Partial<WarehouseComingRowType['product']>) => {
        setAComing((prev) =>
            prev.map((row, i) =>
                i !== index
                    ? row
                    : {
                          ...row,
                          product: {
                              ...row.product,
                              ...patch,
                          },
                      },
            ),
        );
    };

    const isEditing = (index: number, field: 'name' | 'article' | 'price' | 'count') =>
        editField?.index === index && editField.field === field;

    const getPaymentFileUrl = (coming: WarehouseComingRowType): string => {
        const aiUrl = String(coming?.coming?.aiMeta?.cloudFile?.publicUrl || '');
        if (aiUrl) return aiUrl;
        const cloudFiles = coming?.coming?.paymentAttachmentsCloud;
        if (Array.isArray(cloudFiles) && cloudFiles.length) {
            return String(cloudFiles[0]?.publicUrl || '');
        }
        return '';
    };

    const buildPaymentDescriptionFromAi = (coming: WarehouseComingRowType): string => {
        const aiMeta = coming?.coming?.aiMeta;
        if (!aiMeta?.fileName) return String(coming?.coming?.paymentDescription || '');
        return `${t('labelAiDocPrefix')}: ${aiMeta.fileName}, ${t('labelAiDocLine')}: ${Number(aiMeta.sourceLine || 0)}`;
    };

    const handleTogglePayment = (index: number, checked: boolean, coming: WarehouseComingRowType) => {
        if (!checked) {
            handleChangeRow(index, {
                payment: false,
                paymentDescription: '',
                paymentAttachmentsCloud: [],
            });
            return;
        }

        const cloudFile = coming?.coming?.aiMeta?.cloudFile;
        const existingCloud = Array.isArray(coming?.coming?.paymentAttachmentsCloud)
            ? coming.coming.paymentAttachmentsCloud
            : [];
        handleChangeRow(index, {
            payment: true,
            paymentDescription:
                String(coming?.coming?.paymentDescription || '').trim() ||
                buildPaymentDescriptionFromAi(coming),
            paymentAttachmentsCloud:
                existingCloud.length > 0
                    ? existingCloud
                    : cloudFile
                      ? [cloudFile]
                      : [],
        });
    };

    const handleOpenPaymentFile = (e: MouseEvent, coming: WarehouseComingRowType) => {
        e.preventDefault();
        e.stopPropagation();
        const fileUrl = getPaymentFileUrl(coming);
        if (!fileUrl) return;
        if (typeof window !== 'undefined') {
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
        }
    };
 
    return (
        <tbody>
            {aComing?.map((coming,index)=>{
                const canEdit = editable && !comingProcess;
                const isNewProduct = Number(coming.product?.ID || 0) <= 0;
                const paymentFileUrl = getPaymentFileUrl(coming);
                const article = String(coming.product?.article || '').trim();
                return (
                    <tr key={`${coming.product?.ID ?? 'row'}-${index}`}>
                        <td className={`name${isEditing(index, 'name') || isEditing(index, 'article') ? ' is-editing' : ''}`}>
                            {canEdit && isNewProduct && isEditing(index, 'name') ? (
                                <FormInput
                                    autoFocus={true}
                                    autoSelect={true}
                                    compact={true}
                                    showForm={true}
                                    required={true}
                                    setShowForm={(show) => {
                                        if (!show) setEditField(null);
                                    }}
                                    defaultValue={coming.product?.label || ''}
                                    callBack={(value) => {
                                        const nextName = String(value ?? '').trim();
                                        if (!nextName) return false;
                                        handleChangeProduct(index, { label: nextName });
                                        setEditField(null);
                                    }}
                                />
                            ) : (
                                <ProductTitle
                                    showArticle={!isNewProduct}
                                    onClick={
                                        canEdit && isNewProduct
                                            ? () => setEditField({ index, field: 'name' })
                                            : undefined
                                    }
                                    product={{
                                        prodName: coming.product.label,
                                        vendorName: coming.product.vendor,
                                        prodArticle: coming.product.article,
                                        prodStatus: 1,
                                    } as any}
                                />
                            )}
                            {isNewProduct && !(canEdit && isEditing(index, 'name')) && (
                                canEdit && isEditing(index, 'article') ? (
                                    <FormInput
                                        autoFocus={true}
                                        autoSelect={true}
                                        compact={true}
                                        showForm={true}
                                        setShowForm={(show) => {
                                            if (!show) setEditField(null);
                                        }}
                                        defaultValue={article}
                                        callBack={(value) => {
                                            handleChangeProduct(index, { article: String(value ?? '').trim() });
                                            setEditField(null);
                                        }}
                                    />
                                ) : (
                                    <span
                                        className={`f-12 text-muted d-inline-block mt-1${canEdit ? ' coming-editable' : ''}`}
                                        role={canEdit ? 'button' : undefined}
                                        tabIndex={canEdit ? 0 : undefined}
                                        title={t('fields.article')}
                                        onClick={() => canEdit && setEditField({ index, field: 'article' })}
                                        onKeyDown={(e) => {
                                            if (!canEdit) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setEditField({ index, field: 'article' });
                                            }
                                        }}
                                    >
                                        {article || '—'}
                                    </span>
                                )
                            )}
                        </td> 
                        
                        {!hidePrice && 
                            (<td className={`price${isEditing(index, 'price') ? ' is-editing' : ''}`}>
                                <DualPriceCell
                                    row={{ price: Number(coming?.coming?.price || 0) }}
                                    rate={rate}
                                    disabled={!canEdit}
                                    isEditing={isEditing(index, 'price')}
                                    editRatePrice={editRatePrice}
                                    onOpen={(ratePrice) => {
                                        setEditRatePrice(ratePrice);
                                        setEditField({ index, field: 'price' });
                                    }}
                                    onClose={() => {
                                        setEditRatePrice(false);
                                        setEditField(null);
                                    }}
                                    onSave={(value) => {
                                        const nextPrice = saleUsdFromEditValue(value, rate, editRatePrice);
                                        const nextRate = calcSaleRate(nextPrice, rate);
                                        handleChangeRow(index, {
                                            price: nextPrice,
                                            priceRate: nextRate,
                                            ...(isNewProduct
                                                ? { priceSale: nextPrice, priceSaleRate: nextRate }
                                                : {}),
                                        });
                                        setEditRatePrice(false);
                                        setEditField(null);
                                    }}
                                />
                                {coming?.coming?.aiMeta?.mode === 'new' && (
                                    <span className="badge bg-light-warning mt-1">{t('aiBadge.new')}</span>
                                )}
                                {coming?.coming?.aiMeta?.mode === 'similar' && (
                                    <span className="badge bg-light-info mt-1">{t('aiBadge.similar')}</span>
                                )}
                                {coming?.coming?.aiMeta?.mode === 'exact' && (
                                    <span className="badge bg-light-success mt-1">{t('aiBadge.exact')}</span>
                                )}
                            </td>)} 

                        <td className={`count text-end fw-medium${isEditing(index, 'count') ? ' is-editing' : ''} ${Number(coming.coming.cCount) < 0 ? '_text-danger' : ''}`}>
                            {canEdit && isEditing(index, 'count') ? (
                                <FormInput
                                    compact={true} 
                                    autoFocus={true}
                                    autoSelect={true}
                                    showForm={true}
                                    setShowForm={(show) => {
                                        if (!show) setEditField(null);
                                    }}
                                    defaultValue={Number(coming?.coming?.cCount || 0)}
                                    numbersOnly={true}
                                    callBack={(value) => {
                                         
                                        let nextCount = safeIntParse(value) ?? 0;
                                        const maxRaw =
                                            typeof getMaxCount === 'function'
                                                ? getMaxCount(coming)
                                                : null;
                                        const max = Number(maxRaw);
                                        
                                        if (Number.isFinite(max) && max >= 0 && (maxRaw!==null)) {
                                            nextCount = Math.min(nextCount, Math.floor(max));
                                        }
                                         
                                        if (nextCount < 0) nextCount = 0;
                                        handleChangeRow(index, {
                                            cCount: nextCount,
                                        });
                                        setEditField(null);
                                    }}
                                />
                            ) : (
                                <div className="d-flex justify-content-end align-items-center">
                                    <div
                                        onClick={() => canEdit && setEditField({ index, field: 'count' })}
                                        className={canEdit ? 'cursor-pointer' : undefined}
                                    >
                                        <CountBadge count={Number(coming.coming.cCount)}/>
                                    </div>
                                </div>
                            )}
                        </td>
                        {showPayment && (
                            <td className="payment text-center">
                                <div className="d-inline-flex align-items-center gap-1">
                                    <input
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        checked={Boolean(coming?.coming?.payment)}
                                        disabled={!canEdit}
                                        onChange={(e) => handleTogglePayment(index, e.target.checked, coming)}
                                        aria-label={t('labelPayment')}
                                    />
                                    {Boolean(coming?.coming?.payment) && paymentFileUrl ? (
                                        <span
                                            className="d-inline-flex text-success cursor-pointer"
                                            onClick={(e) => handleOpenPaymentFile(e, coming)}
                                            role="button"
                                            title={t('common:labelAttachments')}
                                        >
                                            <SolarIcon name="paperclip" />
                                        </span>
                                    ) : null}
                                </div>
                            </td>
                        )}
                        <td className="action">
                            <Button
                                size="sm"  
                                className="py-0 px-1 mb-1"
                                variant="light-danger"
                                disabled={comingProcess}
                                onClick={(e)=>handleClickDelete(e,index)}>
                                <SolarIcon name="trash3" />
                            </Button>
                        </td>
                    </tr>
                )
            })}
           
        </tbody> 
    )
}

export default ComingTableBody;
